import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface User {
  id: string;
  email: string;
  name: string;
  lastName: string;
  // role: 'Admin' | 'Supervisor' | 'Auditor' | 'Viewer' | "ADMIN" | "AUDITOR";
  role?:any;
  roles?:any;
  avatar?: string;
  lastLogin?: string;
  createdAt: string;
  updatedAt: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  token:any,
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signOut: () => void;
  resetPassword: (email: string) => Promise<{ success: boolean; error?: string }>;
  updateProfile: (data: Partial<User>) => Promise<{ success: boolean; error?: string }>;
  setToken: (token: any) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken]  = useState<any>("")
  const [isAdmin, setIsAdmin] = useState(true)

  // Mock user data for demonstration
  const mockUsers = [
    {
      id: '1',
      email: 'admin@example.com',
      password: 'admin123',
      name: 'Admin User',
      role: 'Admin' as const,
      avatar: '',
      lastLogin: '2024-06-15 09:30:00',
      createdAt: '2024-01-01 00:00:00',
      updatedAt: '2024-06-15 09:30:00',
    },
    {
      id: '2',
      email: 'supervisor@example.com',
      password: 'super123',
      name: 'Dr. Sarah Chen',
      role: 'Supervisor' as const,
      avatar: '',
      lastLogin: '2024-06-15 08:15:00',
      createdAt: '2024-01-01 00:00:00',
      updatedAt: '2024-06-15 08:15:00',
    },
    {
      id: '3',
      email: 'auditor@example.com',
      password: 'audit123',
      name: 'Mike Johnson',
      role: 'Auditor' as const,
      avatar: '',
      lastLogin: '2024-06-14 16:45:00',
      createdAt: '2024-01-01 00:00:00',
      updatedAt: '2024-06-14 16:45:00',
    },
  ];

  useEffect(() => {
    // Check for existing session
    const storedUser = localStorage.getItem('auth_user');
    const storedToken = localStorage.getItem('token');

    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setToken(storedToken)
        setUser(userData);
      } catch (error) {
        localStorage.removeItem('auth_user');
      }
    }
    setIsLoading(false);
  }, []);

  const signIn = async (email: string, password: string): Promise<any> => {
    setIsLoading(true);
    
    // Simulate API call delay
    // await new Promise(resolve => setTimeout(resolve, 1000));
     try {
    const response = await fetch(`${import.meta.env.VITE_API_URL}auth/signin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      // credentials: 'include',
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json();

    if (!response.ok) {
      return { success: false, error: data.message || "Sign in failed" };
    }


    console.log(data,"data-data")
    setToken(data?.data?.token)
    setUser(data?.data?.user)
    localStorage.setItem('token', data?.data?.token);
    localStorage.setItem('refresh-token', data?.data?.refreshToken);
    localStorage.setItem('auth_user', JSON.stringify(data?.data?.user));
    setIsLoading(false);
    return { success: true };

     
  } catch (error: any) {
    return { success: false, error: "A network error occurred. Please try again." };

  }finally{
     setIsLoading(false);
  }

    // const mockUser = mockUsers.find(u => u.email === email && u.password === password);
    
    // if (mockUser) {
    //   const { password: _, ...userWithoutPassword } = mockUser;
    //   const authenticatedUser = {
    //     ...userWithoutPassword,
    //     lastLogin: new Date().toISOString(),
    //   };
      
    //   setUser(authenticatedUser);
    //   localStorage.setItem('auth_user', JSON.stringify(authenticatedUser));
    //   setIsLoading(false);
      
    //   return { success: true };
    // } else {
    //   setIsLoading(false);
    //   return { success: false, error: 'Invalid email or password' };
    // }
  };

  const signOut = () => {
    setUser(null);
    localStorage.removeItem('auth_user');
    localStorage.removeItem('token');
    localStorage.removeItem('refresh-token');
  };

  const resetPassword = async (email: string): Promise<{ success: boolean; error?: string }> => {
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1500));

    const userExists = mockUsers.some(u => u.email === email);
    
    if (userExists) {
      return { success: true };
    } else {
      return { success: false, error: 'Email address not found' };
    }
  };

  const updateProfile = async (data: Partial<User>): Promise<{ success: boolean; error?: string }> => {
    if (!user) {
      return { success: false, error: 'User not authenticated' };
    }

    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 800));

    try {
      const updatedUser = {
        ...user,
        ...data,
        updatedAt: new Date().toISOString(),
      };
      
      setUser(updatedUser);
      localStorage.setItem('auth_user', JSON.stringify(updatedUser));
      
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to update profile' };
    }
  };

  const value = {
    user,
    isLoading,
    isAuthenticated: !!user,
    token,
    isAdmin,
    signIn,
    signOut,
    resetPassword,
    updateProfile,
    setToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};